import { motion } from "framer-motion";
import { useState } from "react";
import { Sun, Leaf, BookOpen, Sparkles, CalendarDays, Layers, CalendarRange, Settings, Shield, LogOut, KeyRound, Globe } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { ROLE_LABELS, ROLE_COLORS } from "../lib/roles";
import { ChangePasswordModal } from "./ChangePasswordModal";

const ITEMS = [
  { id: "today", label: "Oggi", icon: Sun, color: "#f1f5f9" },
  { id: "overview", label: "Panoramica Globale", icon: Globe, color: "#F59E0B" },
  { id: "wellness", label: "Business Wellness", icon: Leaf, color: "#10B981" },
  { id: "book", label: "Progetto Libro", icon: BookOpen, color: "#3B82F6" },
  { id: "ai", label: "AI allo Specchio", icon: Sparkles, color: "#8B5CF6" },
  { id: "plan", label: "Piano AI", icon: CalendarRange, color: "#F59E0B" },
  { id: "calendar", label: "Calendario", icon: CalendarDays, color: "#f1f5f9" },
  { id: "settings", label: "I Miei Link", icon: Settings, color: "#f1f5f9" },
  { id: "users", label: "Utenti", icon: Shield, color: "#f1f5f9" },
];

export function Sidebar({ view, onChange, allowedViews = [] }) {
  const { user, logout } = useAuth();
  const items = ITEMS.filter((i) => allowedViews.includes(i.id));
  const [cpOpen, setCpOpen] = useState(false);

  return (
    <>
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex flex-col w-64 shrink-0 h-screen sticky top-0 border-r border-white/10 bg-[#0c0e14] px-4 py-6">
        <div className="flex items-center gap-3 px-2 mb-8">
          <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-violet-500 via-blue-500 to-emerald-500 grid place-items-center">
            <Layers className="h-5 w-5 text-white" />
          </div>
          <div>
            <p className="font-display font-extrabold leading-tight">Content Studio</p>
            <p className="text-xs text-slate-500">Strategia contenuti</p>
          </div>
        </div>

        <nav className="flex flex-col gap-1">
          {items.map((item) => {
            const Icon = item.icon;
            const active = view === item.id;
            return (
              <button
                key={item.id}
                data-testid={`nav-${item.id}`}
                onClick={() => onChange(item.id)}
                className="relative flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors text-left"
                style={active ? { background: "rgba(255,255,255,0.06)", color: "#fff" } : { color: "#94a3b8" }}
              >
                {active && (
                  <motion.span layoutId="side-active" className="absolute left-0 top-1.5 bottom-1.5 w-1 rounded-full"
                    style={{ background: item.color }} />
                )}
                <Icon className="h-[18px] w-[18px]" style={{ color: active ? item.color : undefined }} />
                {item.label}
              </button>
            );
          })}
        </nav>

        <div className="mt-auto pt-4">
          <div className="flex items-center gap-2.5 px-2 py-2 rounded-xl bg-white/[0.03] border border-white/5">
            <div className="h-9 w-9 rounded-full grid place-items-center font-display font-bold text-sm shrink-0"
              style={{ background: `${ROLE_COLORS[user?.role]}22`, color: ROLE_COLORS[user?.role] }}>
              {(user?.name || user?.email || "?")[0].toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold truncate" data-testid="current-user-name">{user?.name}</p>
              <p className="text-[11px] truncate" style={{ color: ROLE_COLORS[user?.role] }}>{ROLE_LABELS[user?.role]}</p>
            </div>
            <button data-testid="change-password-button" onClick={() => setCpOpen(true)} title="Cambia password" className="p-2 rounded-lg hover:bg-white/10">
              <KeyRound className="h-4 w-4 text-slate-300" />
            </button>
            <button data-testid="logout-button" onClick={logout} title="Esci" className="p-2 rounded-lg hover:bg-rose-500/20">
              <LogOut className="h-4 w-4 text-rose-400" />
            </button>
          </div>
        </div>
      </aside>

      <ChangePasswordModal open={cpOpen} onClose={() => setCpOpen(false)} />

      {/* Mobile bottom nav */}
      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-40 flex items-center justify-start overflow-x-auto bg-[#0c0e14]/95 backdrop-blur border-t border-white/10 px-1 py-2 gap-1">
        {items.map((item) => {
          const Icon = item.icon;
          const active = view === item.id;
          return (
            <button key={item.id} data-testid={`mnav-${item.id}`} onClick={() => onChange(item.id)}
              className="flex flex-col items-center gap-0.5 px-3 py-1 rounded-lg shrink-0"
              style={{ color: active ? item.color : "#64748b" }}>
              <Icon className="h-5 w-5" />
              <span className="text-[9px] font-medium whitespace-nowrap">{item.label.split(" ")[0]}</span>
            </button>
          );
        })}
        <button data-testid="mnav-change-password" onClick={() => setCpOpen(true)} className="flex flex-col items-center gap-0.5 px-3 py-1 rounded-lg shrink-0 text-slate-300">
          <KeyRound className="h-5 w-5" />
          <span className="text-[9px] font-medium">Password</span>
        </button>
        <button data-testid="mnav-logout" onClick={logout} className="flex flex-col items-center gap-0.5 px-3 py-1 rounded-lg shrink-0 text-rose-400">
          <LogOut className="h-5 w-5" />
          <span className="text-[9px] font-medium">Esci</span>
        </button>
      </nav>
    </>
  );
}
